import type { Facets, FacetType } from '@seek/chalice-types';
import {
  Button,
  Inline,
  Stack,
  Text,
  Box,
  IconAI,
  TextLinkButton,
  useResponsiveValue,
  useToast,
  Heading,
} from 'braid-design-system';
import { useState, useMemo } from 'react';

import AnimatedHeight from 'src/components/AnimatedHeight/AnimatedHeight';
import { getRefineSearchQuery } from 'src/components/Search/utils/utils';
import { useHotjarEvent } from 'src/hooks/useHotjarEvent';
import { useDispatch, useSelector } from 'src/store/react';
import { updateKeywordsField } from 'src/store/search';
import { useRunSearch } from 'src/store/search/useRunSearch';
import { selectSearchQuery } from 'src/store/selectors';
import { filterFacetNudgePillsParams } from 'src/utils/filterFacetNudgePillsParams';
import { getFacetLabelText } from 'src/utils/getFacetLabelText';

import { NudgePill } from './NudgePill';
import { MAX_VISIBLE_PILLS_COLLAPSED, MAX_DISPLAY_PILLS } from './constants';

import * as styles from './NudgeIntent.css';

interface NudgeIntentProps {
  facetTypesData: FacetType[];
  title: string;
  description: string;
  displayToastMessage: string;
  isMultiSelect: boolean;
  facetKey: keyof Facets;
}

export const NudgeIntent = ({
  facetTypesData,
  title,
  description,
  displayToastMessage,
  isMultiSelect,
  facetKey,
}: NudgeIntentProps) => {
  const dispatch = useDispatch();
  const isMobile =
    useResponsiveValue()({
      mobile: true,
      tablet: false,
    }) || false;

  const showToast = useToast();
  const query = getRefineSearchQuery(useSelector(selectSearchQuery));
  const runSearch = useRunSearch();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState(false);
  const isNudgePillSelected = selectedIds.size > 0;
  // filter out salaryMin with label '0' first as we dont want to show it as a nudge pill
  const filterSalaryMin = facetTypesData.filter(({ label }) => label !== '0');
  const maximumNudgePillsToShow = filterSalaryMin.slice(0, MAX_DISPLAY_PILLS);
  const expandMoreNudgePills =
    maximumNudgePillsToShow.length > MAX_VISIBLE_PILLS_COLLAPSED;

  const numberOfHiddenPills =
    maximumNudgePillsToShow.length - MAX_VISIBLE_PILLS_COLLAPSED;

  const nudgePillsWithActiveState = useMemo(
    () =>
      maximumNudgePillsToShow
        // Filter out pills with empty labels (string or object)
        .filter((pill) => getFacetLabelText(pill.label) !== '')
        .map((pill, index) => ({
          ...pill,
          isActive: selectedIds.has(pill.id),
          index,
        })),
    [maximumNudgePillsToShow, selectedIds],
  );

  const handleNudgePillClick = (pillId: string) => {
    setSelectedIds((previousSelection) => {
      const wasSelected = previousSelection.has(pillId);

      if (isMultiSelect) {
        const newSet = new Set(previousSelection);
        if (wasSelected) {
          newSet.delete(pillId);
        } else {
          newSet.add(pillId);
        }
        return newSet;
      }
      // Single-select
      return wasSelected ? new Set() : new Set([pillId]);
    });
  };

  const nudgePills = nudgePillsWithActiveState
    ?.filter(({ label }) => label !== '0')
    ?.map(({ id, label, count }) => (
      <NudgePill
        id={id}
        key={id}
        label={getFacetLabelText(label, facetKey)}
        count={count}
        isActive={selectedIds.has(id)}
        isMobile={isMobile}
        onClick={handleNudgePillClick}
        facetKey={facetKey}
      />
    ));

  const selectedJobsCount = useMemo(
    () =>
      Array.from(selectedIds).reduce(
        (sum, id) =>
          sum +
          (maximumNudgePillsToShow.find((pill) => pill.id === id)?.count || 0),
        0,
      ),
    [selectedIds, maximumNudgePillsToShow],
  );

  const handleSelectedJobsCountButtonClick = () => {
    if (facetKey === 'distinctTitle') {
      // single-select for distinctTitle
      // convert the set to an array to get value
      const selectedId = Array.from(selectedIds)[0];
      const pillSelected = maximumNudgePillsToShow.find(
        (p) => p.id === selectedId,
      );
      const label = getFacetLabelText(pillSelected?.label);
      dispatch(updateKeywordsField(label));
      dispatch(
        runSearch({
          pathname: '/jobs',
        }),
      );
    } else {
      // Other facetKeys - workType, workArrangement, salaryMin
      dispatch(
        runSearch({
          pathname: '/jobs',
          overrideQuery: {
            ...query,
            ...filterFacetNudgePillsParams(facetKey, selectedIds),
          },
        }),
      );
    }
    showToast({
      message: displayToastMessage,
      tone: 'positive',
    });
  };

  const displayAllNudgePills = nudgePills.slice(0, nudgePills.length);

  const displayMinNudgePills = nudgePills.slice(0, MAX_VISIBLE_PILLS_COLLAPSED);

  useHotjarEvent('srp_in_flow_nudging_experiment');

  return (
    <Box
      paddingTop="medium"
      paddingX="medium"
      boxShadow="borderPromoteLight"
      borderRadius="large"
      data-automation={`nudge-intent-${facetKey}`}
    >
      <Stack space="medium">
        <Stack space="small">
          <Inline space="none" alignY="center">
            <Heading level="4" weight="regular">
              <IconAI tone="promote" />
            </Heading>
            <Text
              weight="strong"
              tone="promote"
              size={isMobile ? 'small' : 'standard'}
            >
              {title}
            </Text>
          </Inline>
          <Text size={isMobile ? 'small' : 'standard'}>{description}</Text>
        </Stack>
        <AnimatedHeight changeKeys={[expanded]}>
          <Inline
            space={{ mobile: 'xxsmall', tablet: 'xsmall' }}
            alignY="center"
          >
            {expanded ? displayAllNudgePills : displayMinNudgePills}

            {expandMoreNudgePills && !expanded && (
              <Box
                data-automation="expandMoreNudgePills"
                component="button"
                onClick={() => setExpanded(true)}
              >
                <Text size="small">
                  <TextLinkButton weight="weak">
                    +{numberOfHiddenPills} more
                  </TextLinkButton>
                </Text>
              </Box>
            )}

            {expandMoreNudgePills && expanded && (
              <Box
                data-automation="expandMoreNudgePills"
                component="button"
                onClick={() => setExpanded(false)}
              >
                <Text size="small">
                  <TextLinkButton weight="weak">Show less</TextLinkButton>
                </Text>
              </Box>
            )}
          </Inline>
        </AnimatedHeight>
        <Box
          className={{
            [styles.buttonContainer]: true,
            [styles.buttonContainerVisible]: isNudgePillSelected,
          }}
        >
          <Button
            data-automation="selectedJobsCountButton"
            onClick={handleSelectedJobsCountButtonClick}
            variant="solid"
            tone="brandAccent"
          >
            {isNudgePillSelected && facetKey !== 'distinctTitle'
              ? `SEEK ${selectedJobsCount.toLocaleString()} jobs`
              : `SEEK`}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};
